'use client'

import { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { cn } from './cnNew'
import { IconButton } from './buttonNew'
import { XIcon } from './iconsNew'

/** Diálogo modal accesible: bloquea el scroll, cierra con Escape y atrapa el foco inicial. */
export function Modal({ open, onClose, title, description, children, footer, size = 'md' }) {
  const { t } = useTranslation()
  const panelRef = useRef(null)

  useEffect(() => {
    if (!open) return undefined

    const onKeyDown = (event) => {
      if (event.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKeyDown)

    const previous = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    requestAnimationFrame(() => panelRef.current?.focus())

    return () => {
      document.removeEventListener('keydown', onKeyDown)
      document.body.style.overflow = previous
    }
  }, [open, onClose])

  if (!open) return null

  const widths = { sm: 'max-w-md', md: 'max-w-2xl', lg: 'max-w-4xl' }

  return (
    <div className="fixed inset-0 z-[100] flex items-end justify-center p-0 sm:items-center sm:p-6">
      <div
        className="absolute inset-0 bg-[rgba(15,38,43,0.42)]"
        onClick={onClose}
        aria-hidden="true"
      />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        tabIndex={-1}
        className={cn(
          'relative flex max-h-[92vh] w-full flex-col overflow-hidden rounded-t-2xl bg-card shadow-[0_24px_60px_rgba(15,38,43,0.28)] outline-none sm:rounded-2xl',
          widths[size]
        )}
      >
        <header className="flex items-start justify-between gap-4 border-b border-border px-5 py-4">
          <div className="min-w-0">
            <h2 className="text-[17px] font-semibold text-foreground">{title}</h2>
            {description && (
              <p className="mt-0.5 text-[13px] leading-relaxed text-muted-foreground">
                {description}
              </p>
            )}
          </div>
          <IconButton label={t('common.close')} onClick={onClose}>
            <XIcon size={18} />
          </IconButton>
        </header>

        <div className="scrollbar-slim flex-1 overflow-y-auto px-5 py-4">{children}</div>

        {footer && (
          <footer className="flex items-center justify-end gap-2 border-t border-border bg-muted/60 px-5 py-3">
            {footer}
          </footer>
        )}
      </div>
    </div>
  )
}
